import { api } from "../index";
import { getDocumentLang } from "../../utils/helpers";

const lessonPath = (lessonId, suffix) =>
  `/api/v1/user/ai/lessons/${lessonId}/${suffix}`;

export const ai = api.injectEndpoints({
  endpoints: (builder) => ({
    getLessonTranscript: builder.query({
      query: (lessonId) => lessonPath(lessonId, "transcript"),
      providesTags: (_r, _e, lessonId) => [
        { type: "AiTranscript", id: lessonId },
      ],
    }),

    generateLessonTranscript: builder.mutation({
      query: (lessonId) => ({
        url: lessonPath(lessonId, "transcript"),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, lessonId) => [
        { type: "AiTranscript", id: lessonId },
      ],
    }),

    getLessonSubtitles: builder.query({
      async queryFn({ lessonId, language }) {
        const token = localStorage.getItem("token");
        const country = localStorage.getItem("country");
        const headers = {
          "Accept-Language": language || getDocumentLang() || "en",
        };
        if (token) headers.Authorization = token;
        if (country) headers["X-Country-Code"] = country;

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || ""}${lessonPath(lessonId, "subtitles")}`,
            { headers },
          );
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            return { error: { status: response.status, data } };
          }
          return { data };
        } catch (e) {
          return { error: { status: "FETCH_ERROR", error: e.message } };
        }
      },
      providesTags: (_r, _e, { lessonId, language }) => [
        { type: "AiSubtitles", id: `${lessonId}-${language || "default"}` },
      ],
    }),

    generateLessonSubtitles: builder.mutation({
      query: (lessonId) => ({
        url: lessonPath(lessonId, "subtitles"),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, lessonId) => [
        { type: "AiSubtitles", id: `${lessonId}-en` },
        { type: "AiSubtitles", id: `${lessonId}-ar` },
        { type: "AiSubtitles", id: `${lessonId}-default` },
      ],
    }),

    getLessonAction: builder.query({
      query: ({ lessonId, type }) => lessonPath(lessonId, `actions/${type}`),
      providesTags: (_r, _e, { lessonId, type }) => [
        { type: "AiAction", id: `${lessonId}-${type}` },
      ],
    }),

    getLessonQuiz: builder.query({
      query: (lessonId) => lessonPath(lessonId, "quiz"),
      providesTags: (_r, _e, lessonId) => [{ type: "AiQuiz", id: lessonId }],
    }),

    generateLessonQuiz: builder.mutation({
      query: ({ lessonId, question_count = 5 }) => ({
        url: lessonPath(lessonId, "quiz"),
        method: "POST",
        body: { question_count },
      }),
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "AiQuiz", id: lessonId },
      ],
    }),

    getAiSessions: builder.query({
      query: () => `/api/v1/user/ai/sessions`,
      providesTags: [{ type: "AiSessions", id: "LIST" }],
    }),

    createAiSession: builder.mutation({
      query: () => ({
        url: `/api/v1/user/ai/sessions`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "AiSessions", id: "LIST" }],
    }),

    deleteAiSession: builder.mutation({
      query: (sessionId) => ({
        url: `/api/v1/user/ai/sessions/${sessionId}`,
        method: "DELETE",
        responseHandler: async (response) => {
          if (response.status === 204) return null;
          const text = await response.text();
          if (!text) return null;
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        },
      }),
      invalidatesTags: [{ type: "AiSessions", id: "LIST" }],
    }),
  }),
});

export const {
  useGetLessonTranscriptQuery,
  useLazyGetLessonTranscriptQuery,
  useGenerateLessonTranscriptMutation,
  useGetLessonSubtitlesQuery,
  useLazyGetLessonSubtitlesQuery,
  useGenerateLessonSubtitlesMutation,
  useGetLessonActionQuery,
  useLazyGetLessonActionQuery,
  useGetLessonQuizQuery,
  useLazyGetLessonQuizQuery,
  useGenerateLessonQuizMutation,
  useGetAiSessionsQuery,
  useCreateAiSessionMutation,
  useDeleteAiSessionMutation,
} = ai;
